import { Component, OnInit } from '@angular/core';
import { CalendarService } from 'src/app/services/calendar.service';
import { ActivatedRoute, Router } from '@angular/router';
import { PortalEvent } from 'src/app/classes/calendar';
import { DomSanitizer } from '@angular/platform-browser';
import { MyFile } from 'src/app/classes/fileInfo';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { UserService } from 'src/app/services/user.service';
import {format} from 'date-fns';
import {MatDialog} from '@angular/material/dialog';
import { FileService } from 'src/app/services/file.service';
import { EventUnsubscribeConfirmationDialog } from '../editors/event-unsubscribe-confirmation-dialog.component';


@Component({
	selector: 'app-event-viewer',
	templateUrl: './event-viewer.component.html',
	styleUrls: [ './event-viewer.component.css' ],
})
export class EventViewerComponent implements OnInit {


	bFirstLoading = true;
	bCommInProgress = false;

	event = new PortalEvent();
	groupNames = "";
	attachedFiles: MyFile[] = [];
	hasResponded = false;
	registrationStatus: 'pending' | 'registered' | 'unregistered' = 'pending';
	bAdmin = this.authenticationService.isAdmin();
	usernameCache: { [key: string]: string } = {};
	mitgliedsnameCache: { [key: string]: string } = {};

	yesUserIds: string[] = [];
	noUserIds: string[] = [];


	constructor(
		private calendarService: CalendarService,
		private activatedRoute: ActivatedRoute,
		public domSanitizer: DomSanitizer,
		private authenticationService: AuthenticationService,
		private userService: UserService,
		private router: Router,
		private matDialog: MatDialog,
		private fileService: FileService
	) {}


  ngOnInit() {

    this.userService.loadMitgliedsnamenCache().subscribe(cache => {
      this.mitgliedsnameCache = cache;
    });

    this.calendarService.getCalendarGroups().subscribe(groups => {

      const calendarGroups = groups;

      this.activatedRoute.queryParams.subscribe(params => {

        if (!params['id']) {
          alert("Wrong URL");
          return;
        }

        this.updateRegistrationState(params['id']);

        this.calendarService.getEventFullInfo(params['id']).subscribe(event => {

			if (!event) {
				this.bCommInProgress = false;
				return;
			}

          const eventGroupIds = event.groups;
          if (eventGroupIds && eventGroupIds.length) {
            const gNames: string[] = [];
            for (const egId of eventGroupIds) {
              const cg = calendarGroups.find(cg => cg.vgId == egId);
              if (cg && cg.vgName) gNames.push(cg.vgName);
            }
            this.groupNames = gNames.join(", ");
          } else this.groupNames = "-";

          this.attachedFiles = event.files;

          this.event = event;

          this.updateRegistrationState(event.id, event);


          if( this.bAdmin && event.assignedUsers ) {
            this.userService.loadUsernameCache().subscribe(cache => {
              this.usernameCache = cache;
              this.sortAndFilterSubscribedIds();
            });
          }

          this.bFirstLoading = false;

        });

      });

    });

  }


  downloadFile(attachedFile: MyFile) {

    this.fileService.downloadFileById(attachedFile.id, attachedFile.file.name);

  }

  private updateRegistrationState(eventId: string, event?: PortalEvent | null) {
    if (!eventId) {
      this.registrationStatus = 'pending';
      this.hasResponded = false;
      return;
    }

    const currentUserId = this.authenticationService.getCurrentUser().ebeId;

    if (event && event.assignedUsers && event.assignedUsers.length) {
      const assignment = event.assignedUsers.find(user => user.ebeId === currentUserId);
      if (assignment) {
        this.registrationStatus = assignment.vbAngemeldet ? 'registered' : 'unregistered';
        this.hasResponded = true;
        return;
      }
    }

    if (this.calendarService.getSubscribedEventIds().includes(eventId)) {
      this.registrationStatus = 'registered';
      this.hasResponded = true;
      return;
    }

    if (this.calendarService.getUnsubscribedEventIds().includes(eventId)) {
      this.registrationStatus = 'unregistered';
      this.hasResponded = true;
      return;
    }

    this.registrationStatus = 'pending';
    this.hasResponded = false;
  }

  get registrationStatusLabel(): string {
    switch (this.registrationStatus) {
      case 'registered':
        return 'Angemeldet';
      case 'unregistered':
        return 'Abgemeldet';
      default:
        return 'Noch keine Anmeldung';
    }
  }

  get registrationButtonLabel(): string {
    return this.registrationStatus === 'registered' ? 'Abmelden' : 'Anmelden';
  }

  get registrationButtonIcon(): string {
    return this.registrationStatus === 'registered' ? 'logout' : 'login';
  }

  get registrationDeadlinePassed(): boolean {
    if (!this.event || !this.event.tmsRegisterUntil) {
      return false;
    }
    return Date.now() > this.event.tmsRegisterUntil;
  }

  get canShowRegistrationAction(): boolean {
    return !this.bAdmin && (!this.registrationDeadlinePassed || this.hasResponded);
  }

  registerToEvent() {

    this.router.navigate(["Veranstaltungen", "anmelden"], {queryParams: {id: this.event.id}});

  }


	private sortAndFilterSubscribedIds() {

		if( !this.event.assignedUsers || !this.event.assignedUsers.length ) {
			this.yesUserIds = [];
			this.noUserIds = [];
			return;
		}

		this.event.assignedUsers
			.map( user => ({
				userId: user.ebeId,
				mitgliedsname: this.mitgliedsnameCache[ user.ebeId ] || 'Unbekannt',
				username: this.usernameCache[ user.ebeId ] || 'Unbekannt'
			}))
			.sort( (a, b) => {

				const mitgliedComparison = a.mitgliedsname.localeCompare(b.mitgliedsname);
				if( mitgliedComparison !== 0 ) return mitgliedComparison;

				const nameComparison = a.username.localeCompare(b.username);
				if( nameComparison !== 0 ) return nameComparison;

				return a.userId.localeCompare(b.userId);

			});

		const yesUserIds = this.event.assignedUsers.filter( u => u.vbAngemeldet ).map( u => u.ebeId );
		this.yesUserIds = [ ...new Set(yesUserIds) ];	// to make sure there are no duplicates
		const noUserIds = this.event.assignedUsers.filter( u => !u.vbAngemeldet ).map( u => u.ebeId );
		this.noUserIds = [ ...new Set(noUserIds) ];	// to make sure there are no duplicates

	}

  seeRegistration(userId: string) {

    if (this.bCommInProgress) return;

    this.router.navigate(['Veranstaltungen', 'anmeldung_übersicht'], {queryParams: {eventId: this.event.id, userId: userId}});

  }

  downloadFormAnswer() {
    const eventStartDate = new Date(Number(this.event.tmsStart));
    const eventStartFormattedValue = format(eventStartDate, 'dd.MM.yyyy');
    const eventEndDate = new Date(Number(this.event.tmsEnd));
    const eventEndFormattedValue = format(eventEndDate, 'dd.MM.yyyy');
    const eventName = this.event.name;
    const csvFileName = eventName + '_' + eventStartFormattedValue + '_' + eventEndFormattedValue + '.csv';
    this.calendarService.downloadFormAnswers(this.event.id, csvFileName);
  }

  seeMyRegistration() {
    if (this.bCommInProgress) return;

    this.router.navigate(['Veranstaltungen', 'anmeldung_übersicht'], {
      queryParams: {
        eventId: this.event.id,
        userId: this.authenticationService.getCurrentUser().ebeId
      }
    });
  }

  downloadICSFile() {
    this.calendarService.downloadICSFile(this.event.id);
  }

	clickKickUser( userId: string ) {

		if( this.bCommInProgress ) return;
		
		this.matDialog.open( EventUnsubscribeConfirmationDialog ).afterClosed().subscribe( b => {

			if( !b ) return;

			this.bCommInProgress = true;
			this.calendarService.unsubscribeUsersFromEvent( this.event.id, [ userId ] ).subscribe( b => {

				this.bCommInProgress = false;
				if( !b ) return;

				const nPos = this.event.assignedUsers.findIndex( u => u.ebeId == userId );
				if( nPos != -1 ) {
					this.event.assignedUsers.splice( nPos, 1 );
				}

				const ebeId = this.authenticationService.getCurrentUser().ebeId;
				if( userId == ebeId ) {
					this.calendarService.removeEventFromSubscribedEvents( this.event.id );
					this.updateRegistrationState(this.event.id, this.event);
				}

				this.sortAndFilterSubscribedIds();

			});

		});

	}

}
